import { useMonitor } from "@suse-o11y/signal";
import React from "react";

import { Button } from "~/components/button";
import { IconPin, IconPinCrossed } from "~/components/icon";
import { SkeletonBox } from "~/components/skeleton/box";
import { cx } from "~/tools/class-variance";

import { useOpenRightPane } from "../../../../../app/state/right-pane/hooks";
import { useRightPaneBoundary } from "../../../../../components/_right-pane-boundary";
import { Tooltip } from "../../../../../components/react/tooltip/Tooltip";

import { usePinnedItemsState } from "./_pinned-items-context";
import { PinnedItem, type PinnedItemData } from "./PinnedItem";

export interface PinningButtonProps {
	item: undefined | PinnedItemData;
	/**
	 * @default false
	 */
	onlyIcon?: boolean;
	/**
	 * @default false
	 */
	disabled?: boolean;
	className?: string;
}

export const PinningButton: React.FC<PinningButtonProps> = React.memo(
	({ item: itemProp, onlyIcon = false, disabled = false, className }) => {
		const monitor = useMonitor();
		const rightPaneBoundary = useRightPaneBoundary();
		const pinnedItemsState = usePinnedItemsState();
		const openRightPane = useOpenRightPane();

		if (disabled) {
			return <PinningButtonOrIcon className={className} disabled onlyIcon={onlyIcon} />;
		}

		return (
			<SkeletonBox
				className={cx(onlyIcon ? "skeleton:size-3.75" : "skeleton:h-8 skeleton:w-44", className)}
				when={itemProp}
			>
				{(item) => {
					const isPinned = pinnedItemsState.isPinned(monitor, item);
					const itemLabel = PinnedItem.label(item);

					return (
						<Tooltip title={isPinned ? `Unpin the current ${itemLabel}` : `Pin the current ${itemLabel}`}>
							<PinningButtonOrIcon
								isPinned={isPinned}
								onClick={(event) => {
									if (isPinned) {
										pinnedItemsState.unpin(item);
									} else {
										pinnedItemsState.pin(item);

										// do not open the tab if the click goes from the right pane
										if (!rightPaneBoundary) {
											openRightPane("pinned-items");
										}
									}

									// the (un)pin click should never propagate to the parent
									event.stopPropagation();
								}}
								onlyIcon={onlyIcon}
							/>
						</Tooltip>
					);
				}}
			</SkeletonBox>
		);
	},
);

const PinningButtonOrIcon: React.FC<{
	isPinned?: boolean;
	onlyIcon: boolean;
	onClick?: React.MouseEventHandler<HTMLButtonElement>;
	disabled?: boolean;
	className?: string;
}> = ({ isPinned, onlyIcon, className, ...props }) => {
	const icon = isPinned ? <IconPinCrossed className="text-15" /> : <IconPin className="text-15" />;

	if (onlyIcon) {
		return (
			<button
				type="button"
				{...props}
				className={cx(
					"flex items-center justify-center border-0 bg-transparent p-0 outline-hidden transition-colors hover:cursor-pointer hover:text-primary-600 disabled:cursor-default disabled:text-grey-500",
					className,
				)}
			>
				{icon}
			</button>
		);
	}

	return (
		<Button variant="tertiary" {...props} className={cx("gap-2", className)}>
			{icon}
			{isPinned ? "Remove from pinned items" : "Add to pinned items"}
		</Button>
	);
};
